import * as QuickWinsApi from "@/api/useTriage"
import { RESOURCES_NAMES } from "@/const/resource.constants"
import { useCreate, useLoadMore, useQueryClient } from "@/hooks/useHttp"
import { getQuickWinsSelector } from "./useQuickWins.selector"

import type { QuickWinIssueDetail, QuickWinType } from "@/domain/issue"
import type { QuickWinSortField, QuickWinsTab } from "./useQuickWins.types"

export const useQuickWinsData = (
  projectId: string | undefined,
  options?: {
    tab?: QuickWinsTab
    typeFilter?: QuickWinType
    searchQuery?: string
    sortBy?: QuickWinSortField
    ascending?: boolean
    issueId?: string
    enabled?: boolean
  }
) => {
  const {
    tab = "suggested",
    typeFilter,
    searchQuery,
    sortBy,
    ascending,
    issueId,
    enabled = true
  } = options || {}

  const params: Record<string, unknown> = {
    projectId,
    ignored: tab === "ignored",
    typeFilter,
    searchQuery,
    sortBy,
    ascending,
    issueId
  }

  const context = QuickWinsApi.useQuickWins(params, {
    select: getQuickWinsSelector,
    enabled: enabled && !!projectId
  })

  return context
}

export const useQuickWinIssues = (quickWinId: string, enabled: boolean) => {
  const url = quickWinId
    ? RESOURCES_NAMES.TRIAGE.QUICK_WINS.ISSUES(quickWinId)
    : ""

  const result = useLoadMore<QuickWinIssueDetail>(
    url,
    { quickWinId },
    {
      initialPageParam: 0,
      enabled: enabled && !!quickWinId
    }
  )

  return {
    issues: result.data?.pages ?? [],
    hasNext: result.hasNextPage ?? false,
    isFetching: result.isFetching,
    loadMore: result.fetchNextPage,
    reset: result.refetch
  }
}

export const useQuickWinIgnore = () => {
  const queryClient = useQueryClient()

  const mutation = useCreate(
    RESOURCES_NAMES.TRIAGE.QUICK_WINS.ROOT,
    undefined,
    undefined,
    () => ({
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: [RESOURCES_NAMES.TRIAGE.QUICK_WINS.SUMMARY]
        })
        queryClient.invalidateQueries({
          queryKey: [RESOURCES_NAMES.TRIAGE.QUICK_WINS.ROOT]
        })
      }
    })
  )

  return {
    ignore: (
      quickWinId: string,
      shouldIgnore: boolean,
      callbacks?: { onSuccess?: () => void; onError?: () => void }
    ) => {
      mutation.mutate(
        { needle: `/${quickWinId}/ignore`, data: { ignore: shouldIgnore } } as {
          needle: string
          data: { ignore: boolean }
        },
        { onSuccess: callbacks?.onSuccess, onError: callbacks?.onError }
      )
    },
    isLoading: mutation.isPending
  }
}
